import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  Users, Search, MessageSquare, Clock, BookOpen, 
  Crown, ChevronRight, Plus, X, Eye, Calendar,
  CheckCircle, PlayCircle, User
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '/api';

export default function StudentManagement() {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [selectedLessonForNote, setSelectedLessonForNote] = useState(null);
  const [stats, setStats] = useState({ total_students: 0, total_courses: 0 });

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/instructor/students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(response.data.students || []);
      setStats({
        total_students: response.data.total_students,
        total_courses: response.data.total_courses
      });
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error(isEnglish ? 'Failed to load students' : 'Öğrenciler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentDetail = async (studentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/instructor/students/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedStudent(response.data);
    } catch (error) {
      console.error('Error fetching student detail:', error);
      toast.error(isEnglish ? 'Failed to load student details' : 'Öğrenci detayları yüklenemedi');
    }
  };

  const addNote = async () => {
    if (!noteContent.trim()) {
      toast.error(isEnglish ? 'Please enter a note' : 'Lütfen bir not girin');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/instructor/students/${selectedStudent.student.id}/notes`,
        {
          content: noteContent,
          lesson_id: selectedLessonForNote
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(isEnglish ? 'Note added' : 'Not eklendi');
      setNoteContent('');
      setShowNoteModal(false);
      setSelectedLessonForNote(null);
      
      // Refresh student detail
      fetchStudentDetail(selectedStudent.student.id);
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error(isEnglish ? 'Failed to add note' : 'Not eklenemedi');
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm(isEnglish ? 'Delete this note?' : 'Bu notu silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/api/instructor/students/${selectedStudent.student.id}/notes/${noteId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(isEnglish ? 'Note deleted' : 'Not silindi');
      fetchStudentDetail(selectedStudent.student.id);
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error(isEnglish ? 'Failed to delete note' : 'Not silinemedi');
    }
  };

  const filteredStudents = students.filter(student =>
    student.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(isEnglish ? 'en-US' : 'tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-8 px-4 md:px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{isEnglish ? 'Total Students' : 'Toplam Öğrenci'}</p>
              <p className="text-2xl font-bold">{stats.total_students}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <BookOpen className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{isEnglish ? 'Total Courses' : 'Toplam Kurs'}</p>
              <p className="text-2xl font-bold">{stats.total_courses}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <Crown className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{isEnglish ? 'Premium Students' : 'Premium Öğrenci'}</p>
              <p className="text-2xl font-bold">
                {students.filter(s => s.is_premium).length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Student List */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {isEnglish ? 'Students' : 'Öğrenciler'}
              </CardTitle>
              <CardDescription>
                {isEnglish 
                  ? 'Manage students enrolled in your courses' 
                  : 'Kurslarınıza kayıtlı öğrencileri yönetin'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={isEnglish ? 'Search students...' : 'Öğrenci ara...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Student List */}
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {filteredStudents.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>{isEnglish ? 'No students found' : 'Öğrenci bulunamadı'}</p>
                  </div>
                ) : (
                  filteredStudents.map((student) => (
                    <div
                      key={student.student_id}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors hover:bg-gray-50 ${
                        selectedStudent?.student?.id === student.student_id ? 'border-purple-500 bg-purple-50' : ''
                      }`}
                      onClick={() => fetchStudentDetail(student.student_id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                            {student.profile_image ? (
                              <img 
                                src={student.profile_image} 
                                alt={student.full_name}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <User className="h-5 w-5 text-purple-600" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{student.full_name}</p>
                              {student.is_premium && (
                                <Badge className="bg-yellow-100 text-yellow-700 text-xs">
                                  <Crown className="h-3 w-3 mr-1" />
                                  Premium
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">{student.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-1 text-sm text-gray-500">
                            <BookOpen className="h-4 w-4" />
                            <span>{student.courses?.length || 0} {isEnglish ? 'courses' : 'kurs'}</span>
                          </div>
                          {student.notes_count > 0 && (
                            <div className="flex items-center gap-1 text-sm text-purple-600 mt-1">
                              <MessageSquare className="h-4 w-4" />
                              <span>{student.notes_count} {isEnglish ? 'notes' : 'not'}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Student Detail Panel */}
        <div className="lg:col-span-1">
          {selectedStudent ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                      {selectedStudent.student.profile_image ? (
                        <img 
                          src={selectedStudent.student.profile_image} 
                          alt={selectedStudent.student.full_name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <User className="h-6 w-6 text-purple-600" />
                      )}
                    </div>
                    <div>
                      <CardTitle className="text-lg">{selectedStudent.student.full_name}</CardTitle>
                      <p className="text-sm text-gray-500">{selectedStudent.student.email}</p>
                    </div>
                  </div>
                  {selectedStudent.student.is_premium && (
                    <Badge className="bg-yellow-100 text-yellow-700">
                      <Crown className="h-3 w-3 mr-1" />
                      Premium
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Enrolled Courses */}
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <BookOpen className="h-4 w-4" />
                    {isEnglish ? 'Enrolled Courses' : 'Kayıtlı Kurslar'}
                  </h4>
                  <div className="space-y-2">
                    {selectedStudent.purchased_courses?.map((course) => (
                      <div key={course.lesson_id} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-medium text-sm">{course.title}</p>
                          <Badge variant="outline" className="text-xs">
                            {course.lesson_type === 'live' ? (
                              <><PlayCircle className="h-3 w-3 mr-1" /> Canlı</>
                            ) : (
                              <><Clock className="h-3 w-3 mr-1" /> Kayıt</>
                            )}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(course.purchase_date)}
                          </span>
                          <span className="flex items-center gap-1">
                            {course.completed ? (
                              <><CheckCircle className="h-3 w-3 text-green-500" /> {isEnglish ? 'Completed' : 'Tamamlandı'}</>
                            ) : (
                              <>%{Math.round(course.watch_progress)} {isEnglish ? 'watched' : 'izlendi'}</>
                            )}
                          </span>
                        </div>
                        {/* Progress Bar */}
                        <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${course.completed ? 'bg-green-500' : 'bg-purple-500'}`}
                            style={{ width: `${course.watch_progress}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Notes Section */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      {isEnglish ? 'Notes' : 'Notlar'}
                    </h4>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowNoteModal(true)}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      {isEnglish ? 'Add' : 'Ekle'}
                    </Button>
                  </div>
                  
                  {selectedStudent.notes?.length > 0 ? (
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {selectedStudent.notes.map((note) => (
                        <div key={note.id} className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <div className="flex items-start justify-between">
                            <p className="text-sm">{note.content}</p>
                            <button
                              onClick={() => deleteNote(note.id)}
                              className="text-gray-400 hover:text-red-500 ml-2"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatDate(note.created_at)}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">
                      {isEnglish ? 'No notes yet' : 'Henüz not yok'}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-gray-500">
                <Eye className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>{isEnglish ? 'Select a student to view details' : 'Detayları görmek için bir öğrenci seçin'}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Add Note Modal */}
      {showNoteModal && selectedStudent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {isEnglish ? 'Add Note' : 'Not Ekle'}
              </h3>
              <button onClick={() => setShowNoteModal(false)}>
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  {isEnglish ? 'Course (Optional)' : 'Kurs (Opsiyonel)'}
                </label>
                <select
                  value={selectedLessonForNote || ''}
                  onChange={(e) => setSelectedLessonForNote(e.target.value || null)}
                  className="w-full p-2 border rounded-lg"
                >
                  <option value="">{isEnglish ? 'General Note' : 'Genel Not'}</option>
                  {selectedStudent.purchased_courses?.map((course) => (
                    <option key={course.lesson_id} value={course.lesson_id}>
                      {course.title}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">
                  {isEnglish ? 'Note' : 'Not'}
                </label>
                <Textarea
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  placeholder={isEnglish ? 'Enter your note...' : 'Notunuzu girin...'}
                  rows={4}
                />
              </div>
              
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowNoteModal(false)}>
                  {isEnglish ? 'Cancel' : 'İptal'}
                </Button>
                <Button onClick={addNote}>
                  {isEnglish ? 'Save Note' : 'Notu Kaydet'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
